import { useState, useEffect } from "react";
import { format, parseISO } from "date-fns";
import { Calendar as CalendarIcon, Clock, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { updateFlightDatetime } from "../../api/orders";
import { toast } from "sonner";

interface SingleDatePickerProps {
  id?: string;
  value: string;
  onChange: (date: string) => void;
  placeholder?: string;
  minDate?: Date;
}

function SingleDatePicker({
  id,
  value,
  onChange,
  placeholder = "ДД.ММ.ГГГГ",
  minDate,
}: SingleDatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  const disabledBefore = minDate ?? new Date();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          id={id}
          className="w-full justify-between font-normal"
        >
          {selected ? (
            format(selected, "dd.MM.yyyy")
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <CalendarIcon className="w-4 h-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(date) => {
            if (date) {
              onChange(format(date, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          disabled={{ before: disabledBefore }}
        />
      </PopoverContent>
    </Popover>
  );
}

interface TimeInputProps {
  id?: string;
  value: string;
  onChange: (time: string) => void;
}

function TimeInput({ id, value, onChange }: TimeInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
    const formatted =
      digits.length >= 3
        ? `${digits.slice(0, 2)}:${digits.slice(2)}`
        : digits;
    onChange(formatted);
  };

  const handleBlur = () => {
    const match = value.match(/^(\d{2}):(\d{2})$/);
    if (match && parseInt(match[1] ?? "99") <= 23 && parseInt(match[2] ?? "99") <= 59) return;
    if (value !== "") onChange("");
  };

  return (
    <Input
      id={id}
      type="text"
      inputMode="numeric"
      value={value}
      placeholder="ЧЧ:ММ"
      maxLength={5}
      className="pr-10 pl-8"
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
}

interface ChangeDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrderIds: Set<string>;
  onSuccess: (
    updatedIds: string[],
    departureAt: string,
    arrivalAt: string
  ) => void;
}

export function ChangeDateModal({
  isOpen,
  onClose,
  selectedOrderIds,
  onSuccess,
}: ChangeDateModalProps) {
  const [departure, setDeparture] = useState({ date: "", time: "" });
  const [arrival, setArrival] = useState({ date: "", time: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDeparture({ date: "", time: "" });
      setArrival({ date: "", time: "" });
    }
  }, [isOpen]);

  const handleDepartureDate = (date: string) =>
    setDeparture((prev) => ({ ...prev, date }));

  const handleArrivalDate = (date: string) =>
    setArrival((prev) => ({ ...prev, date }));

  const isValid = departure.date !== "" && arrival.date !== "";

  const handleSubmit = async () => {
    const payload = {
      orderIds: [...selectedOrderIds].map(Number),
      departureAt: `${departure.date}T${departure.time || "00:00"}:00Z`,
      arrivalAt: `${arrival.date}T${arrival.time || "00:00"}:00Z`,
    };

    setIsSubmitting(true);
    try {
      await updateFlightDatetime(payload);
      toast.success("Даты у выбранных заказов были успешно обновлены");
      onSuccess([...selectedOrderIds], payload.departureAt, payload.arrivalAt);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Ошибка");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Изменить дату и время рейсов</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Количество выбранных заказов — {selectedOrderIds.size}. Укажите новые
          дату и время вылета для всех выбранных заказов.
        </p>

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium mb-2">Вылет</legend>
          <div className="flex gap-4">
            <div className="flex-1 space-y-1">
              <Label htmlFor="departure-date">Дата вылета</Label>
              <SingleDatePicker
                id="departure-date"
                value={departure.date}
                onChange={handleDepartureDate}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label htmlFor="departure-time">Время вылета</Label>
              <div className="relative">
                <TimeInput
                  id="departure-time"
                  value={departure.time}
                  onChange={(time) =>
                    setDeparture((prev) => ({ ...prev, time }))
                  }
                />
                <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium mb-2">Прилет</legend>
          <div className="flex gap-4">
            <div className="flex-1 space-y-1">
              <Label htmlFor="arrival-date">Дата прилета</Label>
              <SingleDatePicker
                id="arrival-date"
                value={arrival.date}
                onChange={handleArrivalDate}
                minDate={departure.date ? parseISO(departure.date) : undefined}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label htmlFor="arrival-time">Время прилета</Label>
              <div className="relative">
                <TimeInput
                  id="arrival-time"
                  value={arrival.time}
                  onChange={(time) =>
                    setArrival((prev) => ({ ...prev, time }))
                  }
                />
                <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          </div>
        </fieldset>

        <div className="text-sm text-muted-foreground border rounded-md p-3">
          Время будет отображаться в часовом поясе аэропорта вылета.
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button
            disabled={!isValid || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Сохранить изменения"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
